import { Link } from "react-router-dom";
import type { Anime } from "@/types";
import Badge from "@/components/base/Badge";
import AnimeCard from "@/components/base/AnimeCard";
import DetailActions from "./DetailActions";

interface AnimeDetailProps {
  anime: Anime;
  related: Anime[];
}

export default function AnimeDetail({ anime, related }: AnimeDetailProps) {
  const meta = [
    { icon: "ri-calendar-line", label: anime.year },
    { icon: "ri-earth-line", label: anime.region },
    { icon: "ri-play-circle-line", label: `${anime.episodes} 集` },
    { icon: "ri-signal-tower-line", label: anime.status },
  ];

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <div className="mx-auto w-full max-w-[320px] lg:mx-0">
          <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={anime.cover}
              alt={`《${anime.title}》动画封面`}
              title={`${anime.title} 动画详情`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="primary">动画</Badge>
            {anime.tags.map((tag) => (
              <Badge key={tag} tone="secondary">
                {tag}
              </Badge>
            ))}
          </div>
          <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">{anime.title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{anime.description}</p>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-md bg-accent-100 px-3 py-1.5 text-sm font-bold text-accent-800">
              <i className="ri-star-fill"></i>
              评分 {anime.rating.toFixed(1)}
            </span>
            {meta.map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1.5 text-sm text-foreground-600">
                <i className={`${item.icon} text-foreground-400`}></i>
                {item.label}
              </span>
            ))}
          </div>

          <div className="mt-6">
            <DetailActions id={anime.id} title={anime.title} />
          </div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            剧情简介
          </h2>
          <p className="whitespace-pre-line text-sm leading-7 text-foreground-700">
            {anime.synopsis}
          </p>

          <h2 className="mb-4 mt-8 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            配音演员（虚构示例）
          </h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {anime.cast.map((member) => (
              <li
                key={member.actor}
                className="flex items-center gap-3 rounded-lg border border-background-100 bg-background-100 p-3"
              >
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-mic-line text-lg"></i>
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-900">
                    {member.actor}
                  </span>
                  <span className="block text-xs text-foreground-500">{member.role}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <aside className="flex flex-col gap-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-3 text-base font-bold text-foreground-950">分类标签</h2>
            <div className="flex flex-wrap gap-2">
              {anime.genre.map((genre) => (
                <Badge key={genre} tone="primary">
                  {genre}
                </Badge>
              ))}
            </div>
            <Link
              to="/category/recommend"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              查看全部动画推荐
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-3 text-base font-bold text-foreground-950">数据速览</h2>
            <ul className="space-y-2.5 text-sm text-foreground-600">
              <li className="flex items-center justify-between">
                <span>发布时间</span>
                <span className="font-medium text-foreground-800">{anime.publishTime}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>人气热度</span>
                <span className="font-medium text-foreground-800">{anime.views}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>集数</span>
                <span className="font-medium text-foreground-800">{anime.episodes} 集</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 text-lg font-bold text-foreground-950">相关动画推荐</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {related.map((item) => (
              <AnimeCard key={item.id} anime={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}